import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Linkedin, Upload, Users } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * /linkedin — LinkedIn import HISTORY view.
 *
 * As of iter 25 (13 May 2026), per No. 6's "one entrance" canon, the
 * dropzone has been retired. LinkedIn ZIPs now enter via the Shredder,
 * which sniffs the magic bytes / filename and routes to
 * routers.linkedin._import_zip_bytes natively — no LLM, same idempotency
 * by natural key. This page becomes a read-only snapshot ledger plus a
 * link to the rolodex that's built from the imports.
 *
 * Backend lives at /app/backend/routers/linkedin.py.
 */
export default function LinkedInImport() {
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadSnapshots = async () => {
        try {
            const { data } = await api.get("/linkedin/snapshots");
            setSnapshots(data.snapshots || []);
        } catch {
            /* silent — history view */
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadSnapshots(); }, []);

    const totalNew = snapshots.reduce((a, s) => a + (s.total_new || 0), 0);
    const totalSkipped = snapshots.reduce((a, s) => a + (s.total_skipped || 0), 0);

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Bulk Intake · Snapshot History</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                        <Linkedin className="w-10 h-10 text-stamp-red" strokeWidth={1.5} />
                        LinkedIn<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Snapshot ledger of every LinkedIn ZIP filed. Imports now arrive via the
                        Shredder — one entrance, canon-enforced — and are routed to deterministic
                        CSV parsers (no LLM spend, idempotent by natural key).
                    </p>
                </div>
                <div className="pencil text-lg transform -rotate-2">
                    "One slot. One door." — No. 2
                </div>
            </div>

            {/* One-entrance banner */}
            <div
                className="paper-card tape-corner border-2 border-dashed border-ink/30 p-6 text-center mb-6 tilt-l"
                data-testid="linkedin-shredder-banner"
            >
                <Upload className="w-8 h-8 text-ink-soft mx-auto mb-3" strokeWidth={1} />
                <div className="font-heading text-xl uppercase tracking-[0.05em] text-ink">
                    LinkedIn ZIPs enter via the Shredder
                </div>
                <div className="label-typed mt-2">
                    Settings → Data privacy → Get a copy of your data · arrives by email ~24h later
                </div>
                <div className="label-typed text-[9px] mt-1 text-ink-faint">
                    The Shredder sniffs the ZIP, recognises a LinkedIn export, and routes to the
                    native parser — same speed, same idempotency, no LLM tokens spent.
                </div>
                <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
                    <Link
                        to="/shredder"
                        className="border border-stamp-red text-stamp-red px-4 py-2 hover:bg-stamp-red hover:text-paper-light font-heading uppercase text-[11px] tracking-[0.25em] inline-flex items-center gap-2"
                        data-testid="linkedin-shredder-link"
                    >
                        <Upload className="w-3 h-3" /> Open Shredder
                    </Link>
                    <Link
                        to="/contacts"
                        className="border border-ink/40 text-ink px-4 py-2 hover:border-stamp-red hover:text-stamp-red font-heading uppercase text-[11px] tracking-[0.25em] inline-flex items-center gap-2"
                        data-testid="linkedin-contacts-link"
                    >
                        <Users className="w-3 h-3" /> View Contacts
                    </Link>
                </div>
            </div>

            {/* Snapshot history */}
            <div className="paper-card p-5" data-testid="linkedin-snapshots">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                    <div className="label-typed tw-prefix">
                        Import history · {snapshots.length} snapshot{snapshots.length === 1 ? "" : "s"}
                    </div>
                    {snapshots.length > 0 && (
                        <div className="label-typed text-[10px]">
                            <span className="text-stamp-green">{totalNew.toLocaleString()}</span>
                            <span className="text-ink-soft"> new </span>
                            ·
                            <span className="text-ink-soft"> {totalSkipped.toLocaleString()} already on file</span>
                        </div>
                    )}
                </div>
                {loading ? (
                    <div className="font-body text-xs text-ink-faint">Loading…</div>
                ) : snapshots.length === 0 ? (
                    <div className="font-body text-xs text-ink-faint">
                        No imports yet. Drop your first LinkedIn ZIP into the Shredder above.
                    </div>
                ) : (
                    <ul className="space-y-3" data-testid="linkedin-snapshot-list">
                        {snapshots.map((s) => (
                            <li
                                key={s.id}
                                className="border-b border-ink/10 pb-3 last:border-0"
                                data-testid={`linkedin-snapshot-${s.id}`}
                            >
                                <div className="font-heading text-sm text-ink truncate" title={s.filename}>
                                    {s.filename}
                                </div>
                                <div className="label-typed text-[9px] flex items-center gap-2 flex-wrap mt-0.5">
                                    <span>{s.imported_at?.slice(0, 16).replace("T", " ")}</span>
                                    <span>· {(s.size_bytes / 1024 / 1024).toFixed(1)} MB</span>
                                    <span>· {s.status}</span>
                                </div>
                                {s.total_new != null && (
                                    <div className="font-body text-xs text-ink mt-1">
                                        <span className="text-stamp-green">{s.total_new}</span> new
                                        {(s.total_skipped || 0) > 0 && (
                                            <span className="text-ink-soft">
                                                {" "}· {s.total_skipped} already on file
                                            </span>
                                        )}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Shell>
    );
}
